import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api';

const SEVERITY_OPTIONS = [
  { value: 'minor', label: 'Minor', desc: 'Cosmetic damage, no safety concern' },
  { value: 'moderate', label: 'Moderate', desc: 'Functional damage, truck still drivable' },
  { value: 'major', label: 'Major', desc: 'Significant damage, truck may not be safe' },
  { value: 'critical', label: 'Critical', desc: 'Major accident, truck undrivable, possible injuries' },
];

const SEVERITY_COLORS = {
  minor: '#22c55e',
  moderate: '#f59e0b',
  major: '#f97316',
  critical: '#dc2626',
};

function getNowCT() {
  const now = new Date();
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(now);
  const get = (type) => parts.find(p => p.type === type)?.value;
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
}

export default function ReportIncidentPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  // Bucket lock: ?type=incident → locked to Incidents
  //              ?type=accident → locked to Accidents
  //              (no param)     → legacy fallback, allows driver to choose
  // Buckets pass the param when the driver clicks "+ Submit new Incident"
  // or "+ Submit new Accident" from the bucket landing page.
  const lockedType = (() => {
    const t = searchParams.get('type');
    return t === 'incident' || t === 'accident' ? t : null;
  })();
  const isAccident = lockedType === 'accident';
  const isIncident = lockedType === 'incident';
  const bucketBackHref = isAccident ? '/maintenance/accidents'
                        : isIncident ? '/maintenance/incidents'
                        : '/maintenance';
  // User-facing label only — DB still stores incident_type='incident'.
  // "Incident" reads as accident-adjacent to a driver, but operationally
  // this bucket is for truck mechanical issues that didn't reach the
  // severity of an accident. Wording matches that intent.
  const noun       = isAccident ? 'Accident' : isIncident ? 'Truck Issue' : 'Incident / Accident';
  const backNoun   = isAccident ? 'Accidents' : isIncident ? 'Truck Issues' : 'Maintenance';

  const [trucks, setTrucks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [createdId, setCreatedId] = useState(null);
  const [photoFiles, setPhotoFiles] = useState([]);

  const [form, setForm] = useState({
    truck_id: '',
    // Locked to the bucket if a URL param is set, otherwise legacy default.
    incident_type: lockedType || 'incident',
    // Accidents default to moderate (drivers picking the bucket already
    // signal it's not just cosmetic). Incidents default to minor.
    severity: isAccident ? 'moderate' : 'minor',
    title: '',
    description: '',
    location: '',
    occurred_at: getNowCT(),
    injuries: 0,
    injury_details: '',
    police_called: false,
    police_report_number: '',
    third_party_involved: false,
    third_party_details: '',
    tow_needed: false,
    drivable: true,
  });

  const loadTrucks = useCallback(async () => {
    try {
      const data = await api.getTrucks();
      const list = Array.isArray(data) ? data : (data.trucks || []);
      setTrucks(list.filter(t => t.is_active));
    } catch (err) {
      setError('Failed to load trucks');
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadTrucks(); }, [loadTrucks]);

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setForm(f => ({
      ...f,
      [name]: type === 'checkbox' ? checked : value,
    }));
  }

  function handlePhotoChange(e) {
    const files = Array.from(e.target.files);
    if (files.length + photoFiles.length > 10) {
      setError('Maximum 10 photos allowed');
      return;
    }
    setPhotoFiles(prev => [...prev, ...files]);
  }

  function removePhoto(index) {
    setPhotoFiles(prev => prev.filter((_, i) => i !== index));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      // Submit incident
      const incident = await api.createIncident({
        ...form,
        truck_id: parseInt(form.truck_id),
        injuries: parseInt(form.injuries) || 0,
        police_called: form.police_called ? 1 : 0,
        third_party_involved: form.third_party_involved ? 1 : 0,
        tow_needed: form.tow_needed ? 1 : 0,
        drivable: form.drivable ? 1 : 0,
      });

      // Upload photos if any
      if (photoFiles.length > 0 && incident.id) {
        const formData = new FormData();
        photoFiles.forEach(file => formData.append('photos', file));
        await api.uploadIncidentPhotos(incident.id, formData);
      }

      setCreatedId(incident.id);
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Failed to submit incident report');
    }
    setSubmitting(false);
  }

  // Success screen
  if (success) {
    return (
      <div className="inspection-form">
        <div className="inspection-form__complete">
          <div className="inspection-form__complete-icon" style={{ color: '#22c55e' }}>&#10003;</div>
          <h2>{noun} Report Submitted</h2>
          <p style={{ color: 'var(--text-secondary)', marginTop: '8px' }}>
            Report #{createdId} has been filed. Admin and maintenance have been notified via SMS.
          </p>
          <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'center' }}>
            {/* "Report Another" preserves the locked type so a driver who
                opened the Accident form stays on Accidents. */}
            <button className="btn btn-gold" onClick={() => {
              setSuccess(false);
              setCreatedId(null);
              setPhotoFiles([]);
              setForm(f => ({
                ...f,
                title: '', description: '', location: '', occurred_at: getNowCT(),
                injuries: 0, injury_details: '',
                police_called: false, police_report_number: '',
                third_party_involved: false, third_party_details: '',
                tow_needed: false, drivable: true,
                severity: isAccident ? 'moderate' : 'minor',
                incident_type: lockedType || f.incident_type,
              }));
            }}>
              Report Another
            </button>
            <button className="btn btn-outline" onClick={() => navigate(bucketBackHref)}>
              Back to {backNoun}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) return <div className="loading-spinner">Loading...</div>;

  return (
    <div className="incident-form">
      <div className="incident-form__header">
        <h1>Report {noun}</h1>
        <p className="incident-form__subtitle">
          {isAccident
            ? 'File an accident report. Include third-party + police info. Admin and maintenance get an SMS the moment you submit.'
            : isIncident
              ? 'Report a mechanical or operational issue with the truck — anything broken, leaking, or not working right. Maintenance gets an SMS the moment you submit.'
              : 'Fill out all details. Admin and maintenance will be notified immediately.'}
        </p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <form onSubmit={handleSubmit} className="incident-form__body">
        {/* ── Type & Severity ── */}
        <div className="incident-section">
          <h3 className="incident-section__title">
            {lockedType ? 'Severity' : 'Type & Severity'}
          </h3>
          <div className="form-grid">
            {!lockedType && (
              <div className="form-group">
                <label>Report Type *</label>
                <div className="incident-type-toggle">
                  <button
                    type="button"
                    className={`incident-type-btn ${form.incident_type === 'incident' ? 'active' : ''}`}
                    onClick={() => setForm(f => ({ ...f, incident_type: 'incident' }))}
                  >
                    ⚠️ Incident
                  </button>
                  <button
                    type="button"
                    className={`incident-type-btn accident ${form.incident_type === 'accident' ? 'active' : ''}`}
                    onClick={() => setForm(f => ({ ...f, incident_type: 'accident' }))}
                  >
                    🚨 Accident
                  </button>
                </div>
              </div>
            )}
            <div className="form-group">
              <label>Severity *</label>
              <div className="severity-options">
                {SEVERITY_OPTIONS.map(opt => (
                  <label
                    key={opt.value}
                    className={`severity-option ${form.severity === opt.value ? 'selected' : ''}`}
                    style={form.severity === opt.value ? { borderColor: SEVERITY_COLORS[opt.value], background: `${SEVERITY_COLORS[opt.value]}10` } : {}}
                  >
                    <input
                      type="radio"
                      name="severity"
                      value={opt.value}
                      checked={form.severity === opt.value}
                      onChange={handleChange}
                    />
                    <span className="severity-dot" style={{ background: SEVERITY_COLORS[opt.value] }} />
                    <div>
                      <strong>{opt.label}</strong>
                      <small>{opt.desc}</small>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── Vehicle & Details ── */}
        <div className="incident-section">
          <h3 className="incident-section__title">Vehicle & Details</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Truck *</label>
              <select name="truck_id" value={form.truck_id} onChange={handleChange} required className="form-input">
                <option value="">Select truck...</option>
                {trucks.map(t => (
                  <option key={t.id} value={t.id}>{t.unit_number} — {t.make} {t.model}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>When did this occur? *</label>
              <input name="occurred_at" type="datetime-local" value={form.occurred_at} onChange={handleChange} required className="form-input" />
            </div>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label>Title / Summary *</label>
              <input name="title" value={form.title} onChange={handleChange} required className="form-input" placeholder="Brief summary of what happened" />
            </div>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label>Full Description *</label>
              <textarea name="description" value={form.description} onChange={handleChange} required className="form-input" rows={4} placeholder="Describe exactly what happened, where, and how. Include road conditions, weather, speed, etc." />
            </div>
            <div className="form-group">
              <label>Location</label>
              <input name="location" value={form.location} onChange={handleChange} className="form-input" placeholder="Address or intersection" />
            </div>
          </div>
        </div>

        {/* ── Impact Assessment ──
            Truck Issue (incident) bucket = mechanical/operational failure,
            so injuries don't belong here. Keep drivable/tow_needed because
            those drive dispatch's next steps even on a non-accident. */}
        <div className="incident-section">
          <h3 className="incident-section__title">Impact Assessment</h3>
          <div className="form-grid">
            <div className="form-group">
              <label className="checkbox-label">
                <input type="checkbox" name="drivable" checked={form.drivable} onChange={handleChange} />
                <span>Truck is still drivable</span>
              </label>
            </div>
            <div className="form-group">
              <label className="checkbox-label">
                <input type="checkbox" name="tow_needed" checked={form.tow_needed} onChange={handleChange} />
                <span>Truck needs to be towed</span>
              </label>
            </div>
            {!isIncident && (
              <div className="form-group">
                <label>Number of Injuries</label>
                <input name="injuries" type="number" min="0" value={form.injuries} onChange={handleChange} className="form-input" />
              </div>
            )}
            {!isIncident && parseInt(form.injuries) > 0 && (
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label>Injury Details *</label>
                <textarea name="injury_details" value={form.injury_details} onChange={handleChange} className="form-input" rows={3} placeholder="Describe injuries — who, what type, severity, treatment needed" />
              </div>
            )}
          </div>
        </div>

        {/* ── Third Party & Police ──
            Only relevant for Accidents. Hidden entirely on the Truck Issue
            bucket. The legacy combined form (no ?type=) keeps this visible
            for backwards-compat. */}
        {!isIncident && (
        <div className="incident-section">
          <h3 className="incident-section__title">Third Party & Police</h3>
          <div className="form-grid">
            <div className="form-group">
              <label className="checkbox-label">
                <input type="checkbox" name="third_party_involved" checked={form.third_party_involved} onChange={handleChange} />
                <span>Third party involved</span>
              </label>
            </div>
            <div className="form-group">
              <label className="checkbox-label">
                <input type="checkbox" name="police_called" checked={form.police_called} onChange={handleChange} />
                <span>Police were called</span>
              </label>
            </div>
            {form.third_party_involved && (
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label>Third Party Details</label>
                <textarea name="third_party_details" value={form.third_party_details} onChange={handleChange} className="form-input" rows={3} placeholder="Other driver/party name, contact, insurance, vehicle info..." />
              </div>
            )}
            {form.police_called && (
              <div className="form-group">
                <label>Police Report Number</label>
                <input name="police_report_number" value={form.police_report_number} onChange={handleChange} className="form-input" placeholder="Report #" />
              </div>
            )}
          </div>
        </div>
        )}

        {/* ── Photos ── */}
        <div className="incident-section">
          <h3 className="incident-section__title">Photos (Optional)</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: '12px' }}>
            Take photos of the damage, scene, other vehicles, license plates, etc. Up to 10 photos.
          </p>
          <input
            type="file"
            accept="image/*"
            multiple
            capture="environment"
            onChange={handlePhotoChange}
            className="form-input"
          />
          {photoFiles.length > 0 && (
            <div className="incident-photo-preview">
              {photoFiles.map((file, i) => (
                <div key={i} className="incident-photo-thumb">
                  <img src={URL.createObjectURL(file)} alt={`Photo ${i + 1}`} />
                  <button type="button" className="incident-photo-remove" onClick={() => removePhoto(i)}>&times;</button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Submit ── */}
        <div className="incident-form__actions">
          <button type="button" className="btn btn-outline" onClick={() => navigate(bucketBackHref)}>
            Cancel
          </button>
          <button type="submit" className="btn btn-gold" disabled={submitting}>
            {submitting ? 'Submitting…' : `Submit ${noun} Report`}
          </button>
        </div>
      </form>
    </div>
  );
}
